import { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert } from '@components/ui';
import {
  CARD,
  CTA,
  GHOST,
  PAGE_BTN,
  ICON_BTN,
  ICON_BTN_DANGER,
  METRIC_LABEL,
  TONE_SURFACE,
  TONE_BAR,
  ROW_HOVER,
} from '@components/ui/designTokens';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ArrowUturnLeftIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useUsers, useDeleteUser, useReactivateUser, useResendInvitation } from '../hooks/useUsers';
import { CreateUserModal } from './CreateUserModal';
import { EditUserModal } from './EditUserModal';
import type { User } from '../types/user.types';
import { useAuthStore } from '@features/auth/store/authStore';
import { normalizeRole } from '@lib/utils/roleNavigation';
import Swal from 'sweetalert2';

/**
 * Gestión de Usuarios — Claude Design · GestionUsuarios.dc.html
 *
 * El diseño reemplaza la tabla plana por un "ledger" con franja de color por
 * rol y avatar tintado del mismo tono. Ningun dato se perdio: la fila sigue
 * mostrando nombre + ID, correo, telefono, compañia(s), rol(es), estado y las
 * cuatro acciones (editar, enviar invitacion, desactivar y reactivar).
 *
 * Los mocks del diseño llevan un usuario por rol, pero el backend puede devolver
 * usuarios multi-compañia y multi-rol: por eso se preservan las listas
 * completas — todas las compañias como chips con "+N mas" y todos los roles
 * como chips coloreados —, y solo el rol/compañia principales gobiernan la
 * franja y el tint del avatar. La franja de resumen sale unicamente de los
 * usuarios que devolvio la pagina actual (Roles, IsActive, CompanyName), no de
 * un endpoint agregado — el listado responde { users, total, page, pageSize,
 * hasMorePages } y solo `total` es global.
 *
 * Se omite el buscador con atajo ⌘K y los chips de filtro por estado del mock:
 * no hay atajo implementado, y filtrar por Activos/Inactivos solo sobre la
 * pagina cargada mientras la paginacion habla del total del servidor engañaria
 * al usuario. Es la misma decision que tomo el ledger de Clientes.
 */

const roleLabels: Record<string, string> = {
  'VENDEDOR': 'Vendedor',
  'CUSTOMER B2B': 'Cliente B2B',
  'COMPANY ADMIN': 'Administrador de Empresa',
  'ADMINISTRATOR': 'Administrador',
  'admin': 'Administrador',
  'accountant': 'Contador',
  'sales': 'Ventas',
  'user': 'Usuario',
};

/** Familia visual de cada rol (paleta del diseño). `otro` es el neutro para roles no mapeados. */
type RoleKind = 'admin' | 'admin-emp' | 'vendedor' | 'b2b' | 'contador' | 'otro';

const ROLE_KIND: Record<string, RoleKind> = {
  'ADMINISTRATOR': 'admin',
  'admin': 'admin',
  'COMPANY ADMIN': 'admin-emp',
  'VENDEDOR': 'vendedor',
  'sales': 'vendedor',
  'CUSTOMER B2B': 'b2b',
  'accountant': 'contador',
  'user': 'otro',
};

const ROLE_STYLES: Record<
  RoleKind,
  { legend: string; strip: string; swatch: string; chip: string; dot: string; avatar: string }
> = {
  admin: {
    legend: 'Administrador',
    strip: '#0a2233',
    swatch: 'bg-[#0a2233]',
    chip: 'bg-[#0a2233]/[.08] text-[#0a2233] dark:bg-white/10 dark:text-white',
    dot: 'bg-[#0a2233]',
    avatar: 'bg-[#0a2233]/[.10] text-[#0a2233] dark:bg-white/10 dark:text-white',
  },
  'admin-emp': {
    legend: 'Admin. de Empresa',
    strip: '#6b239e',
    swatch: 'bg-[#6b239e]',
    chip: 'bg-[#6b239e]/10 text-[#6b239e]',
    dot: 'bg-[#6b239e]',
    avatar: 'bg-[#6b239e]/10 text-[#6b239e]',
  },
  vendedor: {
    legend: 'Vendedor',
    strip: '#12c4b2',
    swatch: 'bg-teal-500',
    chip: 'bg-teal-500/10 text-[#0d9488]',
    dot: 'bg-teal-500',
    avatar: 'bg-teal-500/10 text-[#0d9488]',
  },
  b2b: {
    legend: 'Cliente B2B',
    strip: '#f5991d',
    swatch: 'bg-accent-500',
    chip: 'bg-accent-500/[.12] text-[#a15f0a]',
    dot: 'bg-accent-500',
    avatar: 'bg-accent-500/[.14] text-[#a15f0a]',
  },
  contador: {
    legend: 'Contador',
    strip: '#d4a814',
    swatch: 'bg-[#d4a814]',
    chip: 'bg-highlight-400/20 text-[#7a5a06]',
    dot: 'bg-[#d4a814]',
    avatar: 'bg-highlight-400/25 text-[#7a5a06]',
  },
  otro: {
    legend: 'Otro',
    strip: '#94a3b8',
    swatch: 'bg-secondary-400',
    chip: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
    dot: 'bg-secondary-400',
    avatar: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
  },
};

/** Orden estable para la leyenda de composicion por rol. */
const ROLE_ORDER: RoleKind[] = ['admin', 'admin-emp', 'vendedor', 'b2b', 'contador', 'otro'];

const kindOf = (role: string): RoleKind => ROLE_KIND[role] ?? 'otro';

/** Conectores que no aportan inicial ("de la Cruz" → LC). */
const INITIAL_STOPWORDS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'e', 'en', 'of', 'the', 'and', 'a']);

const initialsOf = (first?: string, last?: string): string => {
  const source = `${first ?? ''} ${last ?? ''}`.trim();
  if (!source) return '?';
  const words = source
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w) && !INITIAL_STOPWORDS.has(w.toLowerCase()));
  const initials = words.slice(0, 2).map((w) => w[0]).join('');
  return initials.toUpperCase() || '?';
};

const companyInitials = (name?: string): string => {
  if (!name) return '?';
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w) && !INITIAL_STOPWORDS.has(w.toLowerCase()));
  const initials = words.slice(0, 2).map((w) => w[0]).join('');
  return initials.toUpperCase() || '?';
};

/** Circunferencia del arco de "cuentas activas" (r = 24). */
const ARC_LENGTH = 2 * Math.PI * 24;

/**
 * Grilla del ledger: franja + avatar + 5 celdas de datos + acciones.
 *
 * Los minimos son los del contenido, no los "holgados": todas las celdas de
 * texto ya se contienen con `min-w-0` + `truncate` + `title`, asi que apretar
 * los tracks no solapa nada, solo trunca antes. El track de acciones si es fijo
 * y suficiente para las 4 acciones (4 x 32px + 3 x 4px de gap = 140px): con
 * 112px los botones se salian de su celda.
 */
const ROW_GRID =
  'grid min-w-[914px] grid-cols-[4px_40px_minmax(140px,1.6fr)_minmax(150px,1.5fr)_minmax(120px,1.2fr)_minmax(110px,1fr)_96px_148px] items-center gap-x-3';
const COL_HEAD = 'text-[11px] font-bold uppercase leading-[1.25] tracking-[0.09em] text-secondary-400';

export function UserManagerPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // El ID de cada usuario en la tabla solo se muestra si quien está viendo
  // la pantalla (no la fila) tiene el rol Internal Admin — mismo patrón que
  // ya usa BannerAdminPage.tsx para gatear por rol de sesión.
  const sessionUser = useAuthStore((s) => s.user);
  const esInternalAdmin = normalizeRole(sessionUser?.role) === 'INTERNALADMIN';

  const searchQuery = debouncedSearch.length >= 3 || debouncedSearch.length === 0 ? debouncedSearch : '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Paginación y búsqueda reales contra Aludra (ver useUsers.ts / userService.ts)
  const { data, isLoading, isFetching, refetch: refetchUsers, error } = useUsers({
    page: currentPage,
    pageSize: 10,
    search: searchQuery,
  });
  const deleteMutation = useDeleteUser();
  const reactivateMutation = useReactivateUser();
  const resendInvitationMutation = useResendInvitation();

  const users = data?.users ?? [];

  /**
   * Resumen de la pagina actual. Se calcula sobre los usuarios que ya estan en
   * memoria (los mismos que se listan abajo): rol principal, estado y compañia.
   * No hay ninguna consulta extra ni ningun valor estimado.
   */
  const summary = useMemo(() => {
    const byKind: Record<RoleKind, number> = {
      admin: 0,
      'admin-emp': 0,
      vendedor: 0,
      b2b: 0,
      contador: 0,
      otro: 0,
    };
    let active = 0;
    const companyIds = new Set<string>();
    let firstCompany: { id?: string; name?: string; code?: string } | null = null;

    for (const user of users) {
      const primaryRole = (user.roles && user.roles[0]) || user.role;
      byKind[kindOf(primaryRole)] += 1;
      if (user.isActive) active += 1;

      const uc = user.companies && user.companies.length > 0
        ? user.companies
        : (user.companyName ? [{ id: user.companyId, name: user.companyName, code: user.companyCode }] : []);
      for (const c of uc) {
        if (c.id) companyIds.add(c.id);
        if (!firstCompany && c.name) firstCompany = c;
      }
    }

    return { byKind, active, companies: companyIds.size, firstCompany };
  }, [users]);

  const activePct = users.length > 0 ? summary.active / users.length : 0;

  const handleOpenCreate = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = async (user: User) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se desactivará al usuario "${user.firstName} ${user.lastName}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(user.id);
        Swal.fire({
          title: 'Usuario desactivado',
          text: 'El usuario ha sido desactivado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      } catch (error: any) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo desactivar el usuario',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    }
  };

  const handleReactivate = async (user: User) => {
    const result = await Swal.fire({
      title: '¿Reactivar usuario?',
      text: `El usuario "${user.firstName} ${user.lastName}" volverá a estar activo.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        await reactivateMutation.mutateAsync(user.id);
        Swal.fire({
          title: 'Usuario reactivado',
          text: 'El usuario ha sido reactivado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        });
      } catch (error: any) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo reactivar el usuario',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    }
  };

  const handleResendInvitation = async (user: User) => {
    try {
      await resendInvitationMutation.mutateAsync(user.email);
      Swal.fire({
        title: 'Invitación enviada',
        text: `Se envió la invitación a ${user.email}.`,
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    } catch (error: any) {
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || error.message || 'No se pudo enviar la invitación',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  // El rango del pie se calcula con las filas que REALMENTE llegaron, no con
  // `currentPage * pageSize`. Aludra devuelve páginas más cortas que el
  // `pageSize` (su total no sale del mismo universo que las filas), y con la
  // cuenta teórica el pie afirmaba "121 a 130" en una página que traía 6.
  const firstIndex = data && users.length > 0 ? (currentPage - 1) * data.pageSize + 1 : 0;
  const lastIndex = data && users.length > 0 ? firstIndex + users.length - 1 : 0;

  return (
    <div className="font-display">
      {/* ============ CABECERA ============ */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba (quedaba de mas junto al breadcrumb).
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Gestión de Usuarios
            </h1>
          </nav>
          <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Administra los usuarios del sistema
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className={GHOST}
            onClick={() => refetchUsers()}
            data-testid="user-manager-refresh-button"
            disabled={isFetching}
            title="Actualizar lista"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="user-manager-new-button">
            <PlusIcon className="h-3.5 w-3.5" />
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/*
        ============ FRANJA DE RESUMEN ============
        `total` es el unico dato global. Composicion por rol, cuentas activas y
        alcance de compañias salen de los usuarios de la pagina actual.
      */}
      {!isLoading && !error && users.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-secondary-400">
            Resumen de esta página
          </div>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]">
            {/* Usuarios en la pagina + composicion por rol */}
            <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.violet}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.violet}`} />
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className={METRIC_LABEL}>Usuarios del sistema</div>
                  <div className="mt-1.5 flex items-baseline gap-2.5 flex-wrap">
                    <span className="text-[40px] font-extrabold leading-none tracking-[-0.035em] text-ink-850 dark:text-white">
                      {users.length}
                    </span>
                    <span className="whitespace-nowrap text-[14px] text-secondary-500 dark:text-secondary-400">
                      <b className="font-bold text-ink-850 dark:text-white">{summary.active} activo{summary.active === 1 ? '' : 's'}</b>
                      {users.length - summary.active > 0 && (
                        <> · {users.length - summary.active} inactivo{users.length - summary.active === 1 ? '' : 's'}</>
                      )}
                    </span>
                  </div>
                  <div className="mt-1 font-mono text-[12px] text-secondary-400">
                    de {data?.total ?? users.length} en total
                  </div>
                </div>
                {/* Avatar stack: primeros usuarios de la pagina */}
                <div className="flex items-center shrink-0">
                  {users.slice(0, 5).map((user, idx) => {
                    const style = ROLE_STYLES[kindOf((user.roles && user.roles[0]) || user.role)];
                    return (
                      <span
                        key={user.id || idx}
                        title={`${user.firstName} ${user.lastName}`}
                        className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border-2 border-white text-[12px] font-extrabold ${style.avatar} dark:border-secondary-800 ${idx === 0 ? '' : '-ml-2.5'}`}
                      >
                        {initialsOf(user.firstName, user.lastName)}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="mt-[18px]">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                  Por rol
                </div>
                <div className="flex h-2.5 items-center gap-[3px] overflow-hidden rounded-full">
                  {ROLE_ORDER.filter((k) => summary.byKind[k] > 0).map((k) => (
                    <div
                      key={k}
                      className="h-full"
                      style={{ flex: summary.byKind[k], background: ROLE_STYLES[k].strip }}
                      title={`${ROLE_STYLES[k].legend}: ${summary.byKind[k]}`}
                    />
                  ))}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-[22px] gap-y-2 text-[12px] text-secondary-500 dark:text-secondary-400">
                  {ROLE_ORDER.filter((k) => summary.byKind[k] > 0).map((k) => (
                    <span key={k} className="inline-flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-[2px] ${ROLE_STYLES[k].swatch}`} />
                      {ROLE_STYLES[k].legend}{' '}
                      <b className="font-bold text-ink-850 dark:text-white">{summary.byKind[k]}</b>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Actividad: arco de cuentas activas */}
            <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.teal}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.teal}`} />
              <div className={METRIC_LABEL}>Actividad</div>
              <div className="mt-3 flex items-center gap-3.5">
                <svg viewBox="0 0 60 60" className="h-[58px] w-[58px] shrink-0" aria-hidden="true">
                  <circle cx="30" cy="30" r="24" fill="none" stroke="#eef2f6" strokeWidth="6" />
                  <circle
                    cx="30"
                    cy="30"
                    r="24"
                    fill="none"
                    stroke="#0d9488"
                    strokeWidth="6"
                    strokeDasharray={ARC_LENGTH}
                    strokeDashoffset={ARC_LENGTH * (1 - activePct)}
                    strokeLinecap="round"
                    transform="rotate(-90 30 30)"
                  />
                </svg>
                <div>
                  <div className="flex items-baseline gap-[3px]">
                    <span className="text-[28px] font-extrabold leading-none tracking-[-0.03em] text-ink-850 dark:text-white">
                      {summary.active}
                    </span>
                    <span className="text-sm font-semibold text-secondary-400">/{users.length}</span>
                  </div>
                  <div className="mt-1 text-xs leading-[1.35] text-secondary-500 dark:text-secondary-400">
                    cuentas activas
                    <br />
                    <span className="text-secondary-400">{Math.round(activePct * 100)}% de la página</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Alcance de compañia */}
            <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.neutral}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.neutral}`} />
              <div className={METRIC_LABEL}>Alcance</div>
              <div className="mt-3 flex items-center gap-3">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-teal-500 to-[#6b239e] text-[13px] font-extrabold tracking-[-0.02em] text-white">
                  {companyInitials(summary.firstCompany?.name)}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-ink-850 dark:text-white" title={summary.firstCompany?.name}>
                    {summary.firstCompany?.name || '—'}
                  </div>
                  <div className="mt-[3px] font-mono text-[12px] uppercase tracking-[0.06em] text-secondary-400">
                    {summary.firstCompany?.code || '—'} · {summary.companies} EMPRESA{summary.companies === 1 ? '' : 'S'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ BÚSQUEDA ============ */}
      <div className="mt-6">
        <div className="relative rounded-xl border border-[#ececec] bg-white transition-colors focus-within:border-[#0d9488] focus-within:shadow-[0_0_0_3px_rgba(18,196,178,.10)] dark:border-secondary-600 dark:bg-secondary-800">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            placeholder="Buscar usuarios (mínimo 3 caracteres)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="user-manager-search-input"
            className="w-full rounded-xl border-0 bg-transparent py-[13px] pl-11 pr-11 text-sm text-ink-850 outline-none placeholder:text-secondary-400 focus:ring-0 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {search.length > 0 && search.length < 3 && (
          <p className="mt-2 text-[13px] text-secondary-500 dark:text-secondary-400">
            Escribe al menos 3 caracteres para buscar
          </p>
        )}
        {search !== debouncedSearch && search.length >= 3 && (
          <p className="mt-2 flex items-center gap-1.5 text-[13px] text-secondary-500 dark:text-secondary-400">
            <Spinner size="sm" />
            Buscando...
          </p>
        )}
      </div>

      {/* ============ LEDGER ============ */}
      <div className={`${CARD} mt-5 overflow-hidden`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" data-testid="user-manager-loading-spinner" />
          </div>
        ) : error ? (
          <div className="p-5">
            <Alert variant="danger" title="Error al cargar usuarios" data-testid="user-manager-error-alert">
              Ocurrió un error al cargar los usuarios. Por favor, intenta de nuevo.
            </Alert>
          </div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center">
            <UsersIcon className="mx-auto h-12 w-12 text-secondary-300 dark:text-secondary-600" />
            <h3 className="mt-3 text-sm font-bold text-ink-850 dark:text-white">No hay usuarios</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {searchQuery
                ? `No se encontraron resultados para "${searchQuery}"`
                : 'Comienza creando tu primer usuario'}
            </p>
            {searchQuery && (
              <p className="mt-2 text-xs text-secondary-400 dark:text-secondary-500">
                Intenta buscar con menos caracteres o verifica el nombre exacto
              </p>
            )}
            {/*
              Salida de una página vacía. El bloque de paginación vive en la
              rama "hay filas" de este ternario, así que al caer acá no queda
              ningún "Anterior" en pantalla: sin este botón, quien avance a una
              página sin resultados se queda sin forma de volver salvo recargar.
              Se nota al final del listado, donde Aludra deja de devolver filas
              antes de agotar el total que declara.
            */}
            {!searchQuery && currentPage > 1 && (
              <div className="mt-6">
                <button
                  type="button"
                  className={PAGE_BTN}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  data-testid="user-manager-empty-prev-page-button"
                >
                  Volver a la página {currentPage - 1}
                </button>
              </div>
            )}
            {!searchQuery && (
              <div className="mt-6">
                <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="user-manager-empty-new-button">
                  <PlusIcon className="h-4 w-4" />
                  Nuevo Usuario
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/*
              Scroll horizontal en pantallas angostas para no esconder columnas:
              el mock ocultaba Correo / Compañia / Rol por debajo de 1080px y eso
              seria perder informacion visible en la fila.
            */}
            <div className="overflow-x-auto">
              {/* Cabecera */}
              <div
                data-testid="user-manager-table"
                className={`${ROW_GRID} border-b border-[#f0f0f0] bg-secondary-50 py-3.5 pl-0 pr-[22px] dark:border-secondary-700 dark:bg-secondary-900`}
              >
                <div />
                <div />
                <div className={COL_HEAD}>Usuario · ID</div>
                <div className={COL_HEAD}>Correo · Teléfono</div>
                <div className={COL_HEAD}>Compañía</div>
                <div className={COL_HEAD}>Rol</div>
                <div className={COL_HEAD}>Estado</div>
                <div className={`${COL_HEAD} pr-1 text-right`}>Acciones</div>
              </div>

              {users.map((user, index) => {
                const primaryRole = (user.roles && user.roles[0]) || user.role;
                const kind = kindOf(primaryRole);
                const style = ROLE_STYLES[kind];
                const userCompanies = user.companies && user.companies.length > 0
                  ? user.companies
                  : (user.companyName ? [{ id: user.companyId, name: user.companyName, code: user.companyCode }] : []);
                const primaryCompany = userCompanies[0];
                const extraCompanies = userCompanies.slice(1);
                const rolesList = user.roles && user.roles.length > 0 ? user.roles : [user.role].filter(Boolean);
                const extraRoles = rolesList.slice(1);
                const inactive = !user.isActive;

                return (
                  <div
                    key={user.id || `user-${index}`}
                    data-testid={`user-manager-row-${user.id}`}
                    className={`${ROW_GRID} border-t border-dashed border-[#eef0f2] py-3.5 pl-0 pr-[22px] ${ROW_HOVER} first:border-t-0 dark:border-secondary-700`}
                  >
                    {/* Franja de color por rol principal */}
                    <div
                      className="mr-1 h-full w-1 self-stretch"
                      style={{ background: inactive ? '#94a3b8' : style.strip }}
                    />

                    {/* Avatar */}
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="h-10 w-10 rounded-[10px] object-cover"
                      />
                    ) : (
                      <span
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-[10px] text-[14px] font-extrabold tracking-[-0.02em] ${inactive ? 'bg-secondary-200/60 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300' : style.avatar}`}
                        title={roleLabels[primaryRole] || primaryRole}
                      >
                        {initialsOf(user.firstName, user.lastName)}
                      </span>
                    )}

                    {/* Nombre + ID */}
                    <div className="min-w-0">
                      <div
                        className={`truncate text-[15px] font-bold tracking-[-0.01em] ${inactive ? 'text-secondary-500 dark:text-secondary-400' : 'text-ink-850 dark:text-white'}`}
                        title={`${user.firstName} ${user.lastName}`}
                      >
                        {user.firstName} {user.lastName}
                      </div>
                      {esInternalAdmin && user.id && (
                        <div className="mt-[3px] font-mono text-[12px] text-secondary-400">
                          ID {user.id.length > 12 ? `${user.id.substring(0, 8)}…` : user.id}
                        </div>
                      )}
                    </div>

                    {/* Correo + Teléfono */}
                    <div className="min-w-0">
                      <div className={`flex min-w-0 items-center gap-1.5 text-[13px] ${inactive ? 'text-secondary-500 dark:text-secondary-400' : 'text-ink-850 dark:text-white'}`}>
                        <EnvelopeIcon className="h-3 w-3 shrink-0 text-secondary-400" />
                        <span className="truncate" title={user.email || undefined}>
                          {user.email || '-'}
                        </span>
                      </div>
                      <div className="mt-[3px] flex min-w-0 items-center gap-1.5 font-mono text-[12px] text-secondary-500 dark:text-secondary-400">
                        <PhoneIcon className="h-3 w-3 shrink-0 text-secondary-400" />
                        <span className="truncate">{user.phone || '-'}</span>
                      </div>
                    </div>

                    {/* Compañía(s): principal + "+N mas" con tooltip */}
                    <div className="min-w-0">
                      {primaryCompany ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-teal-500 to-[#6b239e] text-[11px] font-extrabold text-white">
                            {companyInitials(primaryCompany.name)}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-semibold text-ink-850 dark:text-white" title={primaryCompany.name}>
                              {primaryCompany.name}
                            </div>
                            {primaryCompany.code && (
                              <div className="font-mono text-[11px] text-secondary-400">
                                {primaryCompany.code}
                              </div>
                            )}
                          </div>
                          {extraCompanies.length > 0 && (
                            <span
                              className="ml-1 inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300"
                              title={extraCompanies.map((c) => c.name).join(', ')}
                            >
                              +{extraCompanies.length}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-sm text-secondary-500 dark:text-secondary-400">
                          <BuildingOfficeIcon className="h-4 w-4 text-secondary-400" />
                          <span>-</span>
                        </div>
                      )}
                    </div>

                    {/* Rol(es): chip principal + "+N mas" con tooltip */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {rolesList.length > 0 ? (
                        <>
                          <span
                            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${style.chip} ${inactive ? 'opacity-60' : ''}`}
                          >
                            <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${style.dot}`} />
                            {roleLabels[primaryRole] || primaryRole}
                          </span>
                          {extraRoles.length > 0 && (
                            <span
                              className="inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300"
                              title={extraRoles.map((r) => roleLabels[r] || r).join(', ')}
                            >
                              +{extraRoles.length}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[13px] text-secondary-400">-</span>
                      )}
                    </div>

                    {/* Estado */}
                    <div>
                      {user.isActive ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-teal-500/10 px-2.5 py-1 text-[12px] font-semibold text-[#0d9488]">
                          <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-[#0d9488] shadow-[0_0_6px_#0d9488]" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-200/50 px-2.5 py-1 text-[12px] font-semibold text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300">
                          <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-secondary-400" />
                          Inactivo
                        </span>
                      )}
                    </div>

                    {/* Acciones: se preservan las 4 acciones del componente
                        original. `flex-wrap` es la red de seguridad: si el track
                        no alcanza, el ultimo boton baja de linea en vez de
                        salirse de la celda. */}
                    <div className="flex flex-wrap justify-end gap-1">
                      {user.isActive && (
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(user)}
                          data-testid={`user-manager-edit-button-${user.id || index}`}
                          className={ICON_BTN}
                          aria-label={`Editar ${user.firstName} ${user.lastName}`}
                          title="Editar usuario"
                          disabled={deleteMutation.isPending}
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {user.isActive && (
                        <button
                          type="button"
                          onClick={() => handleResendInvitation(user)}
                          data-testid={`user-manager-resend-invitation-button-${user.id || index}`}
                          className={ICON_BTN}
                          aria-label={`Enviar invitación a ${user.email}`}
                          title="Enviar invitación"
                          disabled={resendInvitationMutation.isPending}
                        >
                          <PaperAirplaneIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {user.isActive && (
                        <button
                          type="button"
                          onClick={() => handleDelete(user)}
                          data-testid={`user-manager-delete-button-${user.id || index}`}
                          className={ICON_BTN_DANGER}
                          aria-label={`Desactivar ${user.firstName} ${user.lastName}`}
                          title="Desactivar usuario"
                          disabled={deleteMutation.isPending}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {!user.isActive && (
                        <button
                          type="button"
                          onClick={() => handleReactivate(user)}
                          data-testid={`user-manager-reactivate-button-${user.id || index}`}
                          className={ICON_BTN}
                          aria-label={`Reactivar ${user.firstName} ${user.lastName}`}
                          title="Reactivar usuario"
                          disabled={reactivateMutation.isPending}
                        >
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Paginación */}
            {data && (
              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#f0f0f0] bg-secondary-50 px-[22px] py-4 dark:border-secondary-700 dark:bg-secondary-900">
                <div className="text-[13px] text-secondary-500 dark:text-secondary-400">
                  Mostrando <span className="font-bold text-ink-850 dark:text-white">{firstIndex}</span> a{' '}
                  <span className="font-bold text-ink-850 dark:text-white">{lastIndex}</span> de{' '}
                  <span className="font-bold text-ink-850 dark:text-white">{data.total}</span> usuarios
                  <span className="mx-2.5 text-secondary-300">·</span>
                  Página <span className="font-bold text-ink-850 dark:text-white">{currentPage}</span> de{' '}
                  <span className="font-bold text-ink-850 dark:text-white">{totalPages}</span>
                </div>
                <div className="inline-flex gap-2">
                  <button
                    type="button"
                    className={PAGE_BTN}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    data-testid="user-manager-prev-page-button"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={PAGE_BTN}
                    /*
                     * Antes también se deshabilitaba con
                     * `users.length < data.pageSize`, y eso dejaba usuarios
                     * inalcanzables: Aludra devuelve páginas cortas (la 13
                     * traía 6 de 10), así que el botón se apagaba en la 13 de
                     * 15 y las dos últimas páginas —19 usuarios de los 149—
                     * no se podían abrir de ninguna forma.
                     *
                     * Ahora manda `hasMorePages`, que se calcula contra el
                     * total. Si la página siguiente llega sin filas, la
                     * pantalla cae en su estado vacío —que trae su propio
                     * botón para volver— en vez de dejar el listado cortado.
                     */
                    disabled={!data.hasMorePages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    data-testid="user-manager-next-page-button"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Create User */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />

      {/* Modal Edit User */}
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
      />
    </div>
  );
}
